"use client";

/**
 * BBShowreelMosaic — decorative background grid for the /start hero.
 *
 * Gordon: "put a random mosaic in the background... showreal of all image,
 * gif, vu meteres, goniometer, spectogram moving, embedded video urls auto
 * playing... just show bb content, not scraped news."
 *
 * Content comes exclusively from music-platform services (releases, artist
 * profiles, videos) — never from the news feed. The three visualizer tile
 * types (VU, goniometer, spectrogram) don't have an idle/no-audio mode
 * anywhere in the codebase (they're all gated on a real, currently-playing
 * analyser), so autoplaying a real track just to light up a marketing page
 * would be intrusive. These are lightweight, self-animating look-alikes
 * instead — same visual language, no audio required.
 *
 * Tile count is fixed at 32 (a multiple of both the 4-col mobile and 8-col
 * desktop grids) so every row is always full — no ragged last row.
 */

import { useEffect, useRef, useState } from "react";
import { getNewReleases } from "@/services/home";
import { getFeaturedArtists as getFeaturedArtistProfiles } from "@/services/artists";
import { getLatestVideos } from "@/services/videos";
import { extractVideoId, getEmbedUrl, getThumbnailUrl } from "@/lib/youtube";

const VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|$)/i;
const GIF_RE = /\.gif(\?|$)/i;
// Kept modest — this is a decorative background, not the main content. Each
// video/iframe tile is a real decoder; each visualizer tile is a live RAF
// canvas loop. Too many of either at once visibly janks the tab (confirmed
// while building this — scroll/input latency spiked with 32 tiles + a
// ResizeObserver per canvas). 24 is a multiple of both the 4-col mobile and
// 8-col desktop grid, so rows still always come out even.
const TILE_COUNT = 24;
const MAX_LIVE_YOUTUBE = 2;
const MAX_LIVE_NATIVE_VIDEO = 3;

type TileKind = "image" | "gif" | "video-native" | "video-youtube" | "vu" | "goniometer" | "spectrogram";

interface Tile {
  id: string;
  kind: TileKind;
  src?: string;
  youtubeId?: string;
  title?: string;
}

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function BBShowreelMosaic({ className }: { className?: string }) {
  const [tiles, setTiles] = useState<Tile[] | null>(null);
  const isIosRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Only decode video / run canvas RAF loops while the mosaic is actually
  // on screen — once the visitor scrolls past the hero there's no reason to
  // keep paying for a dozen live tiles they can't see.
  const [active, setActive] = useState(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold: 0.1 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    isIosRef.current = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;

    let cancelled = false;
    (async () => {
      const [releases, artists, videos] = await Promise.all([
        getNewReleases().catch(() => []),
        getFeaturedArtistProfiles(24).catch(() => []),
        getLatestVideos(16).catch(() => []),
      ]);
      if (cancelled) return;

      const pool: Tile[] = [];

      releases.forEach((r) => {
        const imgs = (r.images?.length ? r.images : [r.imageUrl]).filter(Boolean) as string[];
        imgs.forEach((url, i) => {
          pool.push({ id: `rel-${r.id}-${i}`, kind: GIF_RE.test(url) ? "gif" : "image", src: url, title: r.title });
        });
      });

      artists.forEach((a) => {
        const imgs = [a.imageUrl, ...(a.images || [])].filter(Boolean) as string[];
        imgs.forEach((url, i) => {
          pool.push({ id: `art-${a.id}-${i}`, kind: GIF_RE.test(url) ? "gif" : "image", src: url, title: a.name });
        });
        if (a.videoUrl) {
          const yid = extractVideoId(a.videoUrl);
          if (yid) pool.push({ id: `art-vid-${a.id}`, kind: "video-youtube", youtubeId: yid, title: a.name });
          else if (VIDEO_RE.test(a.videoUrl)) pool.push({ id: `art-vid-${a.id}`, kind: "video-native", src: a.videoUrl, title: a.name });
        }
      });

      videos.forEach((v) => {
        if (v.youtubeId) pool.push({ id: `vid-${v.videoId}`, kind: "video-youtube", youtubeId: v.youtubeId, title: v.title });
        else if (v.url && VIDEO_RE.test(v.url)) pool.push({ id: `vid-${v.videoId}`, kind: "video-native", src: v.url, title: v.title });
        else if (v.thumbnailUrl) pool.push({ id: `vid-thumb-${v.videoId}`, kind: "image", src: v.thumbnailUrl, title: v.title });
      });

      let shuffled = shuffle(pool);

      // Cap simultaneous live iframes/native videos — demote overflow to a
      // static thumbnail (YouTube) or drop it (native, no thumb available).
      let ytCount = 0;
      let nativeCount = 0;
      shuffled = shuffled
        .map((t) => {
          if (t.kind === "video-youtube") {
            ytCount++;
            if (isIosRef.current || ytCount > MAX_LIVE_YOUTUBE) {
              return { ...t, kind: "image" as const, src: getThumbnailUrl(t.youtubeId!, "hqdefault") };
            }
          }
          if (t.kind === "video-native") {
            nativeCount++;
            if (nativeCount > MAX_LIVE_NATIVE_VIDEO) return null;
          }
          return t;
        })
        .filter((t): t is Tile => t !== null);

      // Fill remaining slots with decorative visualizer tiles (~25% of grid).
      const visKinds: TileKind[] = ["vu", "goniometer", "spectrogram"];
      const contentCount = Math.max(0, Math.round(TILE_COUNT * 0.75));
      const content = shuffled.slice(0, contentCount);
      const visCount = TILE_COUNT - content.length;
      const visTiles: Tile[] = Array.from({ length: visCount }, (_, i) => ({
        id: `vis-${i}`,
        kind: visKinds[i % visKinds.length],
      }));

      setTiles(shuffle([...content, ...visTiles]));
    })();

    return () => { cancelled = true; };
  }, []);

  if (!tiles || tiles.length === 0) return null;

  return (
    <div ref={containerRef} className={className} aria-hidden="true">
      <div className="grid grid-cols-4 sm:grid-cols-8 h-full w-full">
        {tiles.map((tile) => <MosaicTile key={tile.id} tile={tile} active={active} />)}
      </div>
    </div>
  );
}

function MosaicTile({ tile, active }: { tile: Tile; active: boolean }) {
  switch (tile.kind) {
    case "image":
    case "gif":
      return (
        <div className="relative w-full h-full overflow-hidden bg-muted/10">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={tile.src} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      );
    case "video-native":
      return (
        <div className="relative w-full h-full overflow-hidden bg-black/20">
          <NativeVideoTile src={tile.src!} active={active} />
        </div>
      );
    case "video-youtube":
      return (
        <div className="relative w-full h-full overflow-hidden bg-black/20 pointer-events-none">
          {active ? (
            <iframe
              className="absolute inset-0 w-full h-full border-0 scale-[1.6]"
              src={getEmbedUrl(tile.youtubeId!, {
                autoplay: true, mute: true, loop: true, controls: false,
                modestBranding: true, disableKb: true, noFullscreen: true, noCaptions: true,
              })}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              title=""
              tabIndex={-1}
            />
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={getThumbnailUrl(tile.youtubeId!, "hqdefault")} alt="" className="w-full h-full object-cover" loading="lazy" />
          )}
        </div>
      );
    case "vu":
    case "goniometer":
    case "spectrogram":
      return <VisualizerTile variant={tile.kind} active={active} />;
  }
}

function NativeVideoTile({ src, active }: { src: string; active: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) v.play().catch(() => {});
    else v.pause();
  }, [active]);
  return <video ref={videoRef} src={src} muted loop playsInline className="w-full h-full object-cover" />;
}

// ── Decorative canvas visualizers ────────────────────────────────────────────
// Deterministic sine-layered motion (same technique as the real player's iOS
// pulse fallback) — organic-looking without needing a live audio graph.

let _colorCache = { t: 0, primary: "", muted: "" };
function themeColors(): { primary: string; muted: string } {
  const now = Date.now();
  if (now - _colorCache.t > 1000) {
    _colorCache.t = now;
    try {
      const style = getComputedStyle(document.documentElement);
      const p = style.getPropertyValue("--primary").trim();
      const m = style.getPropertyValue("--muted-foreground").trim();
      _colorCache.primary = p ? `hsl(${p})` : "#22c55e";
      _colorCache.muted = m ? `hsl(${m})` : "#888";
    } catch {
      _colorCache.primary = "#22c55e";
      _colorCache.muted = "#888";
    }
  }
  return _colorCache;
}

function VisualizerTile({ variant, active }: { variant: "vu" | "goniometer" | "spectrogram"; active: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const phaseRef = useRef(Math.random() * 1000);
  const specBinsRef = useRef<number[]>(Array.from({ length: 24 }, () => Math.random()));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let raf = 0;
    let lastFrame = 0;
    // ~12fps — this is a small decorative background tile, not a real meter;
    // a ResizeObserver per tile (dozens on screen at once) was firing on
    // every unrelated layout shift elsewhere on the page and visibly janked
    // the tab, so sizing is one-shot on mount plus a single debounced
    // window resize listener instead.
    const frameInterval = 1000 / 12;

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w <= 0 || h <= 0) return;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    };
    resize();
    let resizeTimer = 0;
    const onWindowResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(resize, 200);
    };
    window.addEventListener("resize", onWindowResize);

    const drawVu = (t: number) => {
      const { width: w, height: h } = canvas;
      ctx.clearRect(0, 0, w, h);
      const { primary, muted } = themeColors();
      const phase = phaseRef.current;
      [0, 1].forEach((ch) => {
        const v = 0.5 + 0.34 * Math.sin(t * 0.0021 + phase + ch * 1.7) + 0.12 * Math.sin(t * 0.0053 + phase);
        const level = Math.max(0.05, Math.min(1, v));
        const segCount = 10;
        const segW = w / 2 - w * 0.06;
        const segH = h / segCount;
        const x = ch === 0 ? w * 0.06 : w * 0.5 + w * 0.02;
        for (let i = 0; i < segCount; i++) {
          const litFromBottom = segCount - i;
          const lit = litFromBottom / segCount <= level;
          ctx.fillStyle = lit ? primary : muted;
          ctx.globalAlpha = lit ? 0.85 : 0.18;
          ctx.fillRect(x, i * segH + h * 0.03, segW, segH - h * 0.015);
        }
      });
      ctx.globalAlpha = 1;
    };

    const gonioTrail: { x: number; y: number }[] = [];
    const drawGonio = (t: number) => {
      const { width: w, height: h } = canvas;
      const { primary, muted } = themeColors();
      ctx.fillStyle = "rgba(0,0,0,0.12)";
      ctx.globalCompositeOperation = "source-over";
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2, cy = h / 2, r = Math.min(w, h) * 0.42;
      ctx.strokeStyle = muted;
      ctx.globalAlpha = 0.25;
      ctx.beginPath();
      ctx.moveTo(cx - r, cy); ctx.lineTo(cx + r, cy);
      ctx.moveTo(cx, cy - r); ctx.lineTo(cx, cy + r);
      ctx.stroke();

      const phase = phaseRef.current;
      const tt = t * 0.0016 + phase;
      const x = cx + r * Math.sin(tt * 1.3) * Math.cos(tt * 0.31);
      const y = cy + r * Math.cos(tt * 1.7) * Math.sin(tt * 0.47);
      gonioTrail.push({ x, y });
      if (gonioTrail.length > 26) gonioTrail.shift();

      gonioTrail.forEach((p, i) => {
        ctx.globalAlpha = (i / gonioTrail.length) * 0.9;
        ctx.fillStyle = primary;
        const size = Math.max(1, r * 0.045);
        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
    };

    const drawSpectrogram = (t: number) => {
      const { width: w, height: h } = canvas;
      const { primary } = themeColors();
      // Scroll existing content left by one column.
      const colW = Math.max(1, Math.round(w / 48));
      try {
        const img = ctx.getImageData(colW, 0, w - colW, h);
        ctx.putImageData(img, 0, 0);
      } catch { /* canvas not yet sized */ }
      ctx.clearRect(w - colW, 0, colW, h);

      const bins = specBinsRef.current;
      const phase = phaseRef.current;
      const binH = h / bins.length;
      for (let i = 0; i < bins.length; i++) {
        // Slow random walk per bin, biased toward mid-low energy.
        bins[i] += (Math.random() - 0.5) * 0.06;
        bins[i] = Math.max(0.03, Math.min(1, bins[i]));
        const energy = bins[i] * (0.6 + 0.4 * Math.sin(t * 0.0009 + phase + i * 0.4));
        const clamped = Math.max(0, Math.min(1, energy));
        ctx.globalAlpha = 0.15 + clamped * 0.75;
        ctx.fillStyle = primary;
        ctx.fillRect(w - colW, h - (i + 1) * binH, colW, binH - 0.5);
      }
      ctx.globalAlpha = 1;
    };

    const loop = (t: number) => {
      // Skip drawing (but keep the loop alive) while the tab isn't visible
      // or the tile has scrolled out of view — dozens of idle canvases
      // burning CPU off-screen or in a background tab is wasteful.
      if (!document.hidden && activeRef.current && t - lastFrame >= frameInterval) {
        lastFrame = t;
        if (variant === "vu") drawVu(t);
        else if (variant === "goniometer") drawGonio(t);
        else drawSpectrogram(t);
      }
      if (!reduceMotion) raf = requestAnimationFrame(loop);
    };

    if (reduceMotion) {
      if (variant === "vu") drawVu(0);
      else if (variant === "goniometer") drawGonio(0);
      else drawSpectrogram(0);
    } else {
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", onWindowResize);
    };
  }, [variant]);

  return (
    <div className="relative w-full h-full overflow-hidden bg-background/60">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
}
